import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../../directives/header";
import Footer from "../../directives/footer";
import Sidebar from "../../directives/sidebar";
import { getUserKycById } from "../../Action/action";
import toast, { Toaster } from "react-hot-toast";
import { LiaBirthdayCakeSolid } from "react-icons/lia";
import { GiVikingLonghouse } from "react-icons/gi";
import { IoCodeWorking } from "react-icons/io5";
import {
  MdKeyboardBackspace,
  MdEmail,
  MdPhone,
  MdVerifiedUser,
  MdDateRange,
} from "react-icons/md";
import { FaUser, FaMapMarkerAlt, FaIdCard, FaCity, FaRegAddressBook } from "react-icons/fa";
import config from "../../coreFIles/config";
import { Col, Row } from "react-bootstrap";

/* ================== STATUS HELPER ================== */
const getKycStatus = (status) => {
  switch (status) {
    case 1:
      return { text: "Pending", badge: "warning" };
    case 2:
      return { text: "Approved", badge: "success" };
    case 3:
      return { text: "Rejected", badge: "danger" };
    default:
      return { text: "Unknown", badge: "secondary" };
  }
};

const ViewKycUser = () => {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await getUserKycById(id);
        if (res?.status) {
          setUser(res.data);
        } else {
          toast.error(res?.msg || "Something went wrong");
        }
      } catch (err) {
        toast.error("Failed to fetch KYC user");
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [id]);

  if (loading) {
    return <div className="text-center py-5">Loading...</div>;
  }

  if (!user) return null;

  const kycStatus = getKycStatus(user.status);

  return (
    <div className="wrapper">
      <Header />
      <Sidebar />
      <Toaster />

      <div className="content-wrapper">
        <div className="container-full">
          <section className="content">
            <div className="box">
              {/* HEADER */}
              <div className="box-header d-flex align-items-center">
                <Link to={`${config.baseUrl}kycList`} className="me-3">
                  <MdKeyboardBackspace size={24} />
                </Link>
                <h4 className="mb-0 fw-bold">View KYC User</h4>
              </div>

              <div className="box-body">
                <Row>
                  <Col md={12} className="mb-4">
                    {/* BASIC INFO */}
                    <Section title="Basic Information" />
                    <div className="row">
                      <Info
                        label="Full Name"
                        value={`${user.firstName} ${user.lastName}`}
                        icon={<FaUser />}
                      />
                      <Info
                        label="Email"
                        value={user.email}
                        icon={<MdEmail />}
                      />
                      <Info
                        label="Phone"
                        value={user.phone}
                        icon={<MdPhone />}
                      />
                      <Info
                        label="Birth Date"
                        value={
                          user.birthDate
                            ? new Date(user.birthDate).toLocaleDateString()
                            : "-"
                        }
                        icon={<LiaBirthdayCakeSolid />
                        }
                      />
                    </div>
                    <Divider />
                  </Col>

                  <Col md={12} className="mb-4">
                    {/* ADDRESS */}
                    <Section title="Address Details" />
                    <div className="row">
                      <Info
                        label="Country"
                        value={user.country}
                        icon={<FaMapMarkerAlt />}
                      />
                      <Info label="State" value={user.state} icon={<GiVikingLonghouse />} />
                      <Info label="City" value={user.city} icon={<FaCity />} />
                      <Info label="Postal Code" value={user.postalCode} icon={<IoCodeWorking />} />
                      <Info label="Full Address" value={user.address} full icon={<FaRegAddressBook />} />
                    </div>
                    <Divider />
                  </Col>

                  <Col md={12} className="mb-4">
                    {/* KYC STATUS */}
                    <Section title="KYC Status" />
                    <div className="row">
                      <Info
                        label="KYC Status"
                        value={kycStatus.text}
                        badge={kycStatus.badge}
                        icon={<MdVerifiedUser />}
                      />
                      <Info
                        label="Document Type"
                        value={user.documentType?.toUpperCase()}
                        icon={<FaIdCard />}
                      />
                    </div>
                    <Divider />
                  </Col>

                  <Col md={12} className="mb-4">
                    {/* META INFO */}
                    <Section title="Meta Information" />
                    <div className="row">
                      <Info
                        label="Created At"
                        value={
                          user.createdAt
                            ? new Date(user.createdAt).toLocaleString()
                            : "-"
                        }
                        icon={<MdDateRange />}
                      />
                      <Info
                        label="Updated At"
                        value={
                          user.updatedAt
                            ? new Date(user.updatedAt).toLocaleString()
                            : "-"
                        }
                        icon={<MdDateRange />}
                      />
                    </div>
                    <Divider />
                  </Col>

                  <Col md={12} className="mb-4">
                    {/* DOCUMENTS */}
                    <Section title="KYC Documents" />
                    <div className="row">
                      <DocumentCard
                        title="Document Front"
                        url={user.documentFrontId}
                      />
                      <DocumentCard
                        title="Document Back"
                        url={user.documentBackId}
                      />
                      <DocumentCard
                        title="Selfie With ID"
                        url={user.selfieWithId}
                      />
                    </div>
                    <Divider />
                  </Col>
                </Row>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

/* ================== REUSABLE COMPONENTS ================== */

const Section = ({ title }) => (
  <h5 className="mb-3 fw-bold text-primary">{title}</h5>
);

const Divider = () => <hr className="my-2 mt-4" />;

// const Info = ({ label, value, badge, full, icon }) => (
//   <div className={`col-md-${full ? 12 : 4} mb-3`}>
//     <div
//       className="p-3 h-100"
//       style={{
//         borderRadius: "12px",
//         background: "#f9fafb",
//         border: "1px solid #e5e7eb",
//       }}
//     >
//       <div className="d-flex align-items-center mb-1">
//         {icon && <span className="me-2 text-muted">{icon}</span>}
//         <small className="text-uppercase fw-semibold text-muted">
//           {label}
//         </small>
//       </div>

//       {badge ? (
//         <span className={`badge bg-${badge} px-3 py-2 mt-1`}>
//           {value}
//         </span>
//       ) : (
//         <div className="fw-semibold text-dark mt-1">
//           {value || "-"}
//         </div>
//       )}
//     </div>
//   </div>
// );

const Info = ({ label, value, badge, full, icon }) => (
  <div className="">
    <Row className="my-2">
      <Col xs={6} md={6} lg={6} xl={5} className="pe-0">
        <div className="d-flex align-items-center">
          {icon && <span className="me-2 text-muted">{icon}</span>}
          <div className="fw-semibold d-flex">
            <span>{label}</span>
            <span>&ensp;:</span>
          </div>
        </div>
      </Col>

      <Col xs={6} md={6} lg={6} xl={7}>
        {badge ? (
          <>
            &ensp;
            <span className={`badge text-${badge} p-0 fw-bold`} style={{fontSize:'13px'}}>
              {value}
            </span>
          </>
        ) : (
          <div className="fw-semibold">
            &ensp;{value || "-"}
          </div>
        )}
      </Col>
    </Row>
  </div>
);

const DocumentCard = ({ title, url }) => (
  <div className="col-md-4 mb-3">
    <div
      className="p-3 text-center"
      style={{
        borderRadius: "12px",
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
      }}
    >
      <small className="text-uppercase fw-semibold text-muted d-block mb-2">
        {title}
      </small>

      {url ? (
        <a href={url} target="_blank" rel="noreferrer">
          <img
            src={url}
            alt={title}
            style={{
              width: "100%",
              height: "180px",
              objectFit: "cover",
              borderRadius: "8px",
              border: "1px solid #ddd",
            }}
          />
        </a>
      ) : (
        <div className="text-muted">No Document</div>
      )}
    </div>
  </div>
);

export default ViewKycUser;
